import React from "react";
import {autorun} from "mobx";
import {UiState} from "../../ui-state";
import {observer} from "mobx-react";
import { Icon } from "../icon/icon";

import "./coordinate.scss";
import * as reactiveUtils from "@arcgis/core/core/reactiveUtils";
import Extent from "@arcgis/core/geometry/Extent";

interface CoordinateProps {
    view: __esri.MapView | __esri.SceneView | undefined;
    uiStore: UiState;
}

@observer
export class Coordinate extends React.Component<CoordinateProps, any> {
    autorunDisposers: any[] = [];
    elevationLayerIndex = -1;
    elevationSampler: Promise<__esri.ElevationSampler> | null = null;
    watchHandles: any[] = [];
    widgetKey = "coordinate";
    cursorTimeout = 250;
    cursorTimeoutId: number | null = null;
    constructor(props: CoordinateProps) {
        super(props);
        this.state = {
            isLoading: true,
            coordinate: null,
            titleElevationLayer: null
        };
    }
    resetCursorTimer(): void {
        if (this.cursorTimeoutId) {
            window.clearTimeout(this.cursorTimeoutId);
        }
    }
    loadElevationLayer(): void {
        const {view} = this.props;
        if (!view) {
            return;
        }
        if (view.map.ground.layers.length > 0) {
            // Suche Layer mit der id "dom", ansonsten erster Layer in der ground Liste
            this.elevationLayerIndex =
                Math.max(0, view.map.ground.layers.findIndex((e)=> e.id==="dom" ));
            if (view.map.ground.layers.getItemAt(this.elevationLayerIndex).title
                !== this.state.titleElevationLayer) {
                this.updateElevationSampler();
            }
        }
    }
    componentDidMount(): void {
        // rerun until map view is ready and layer list view model is loaded
        this.autorunDisposers.push(autorun((reaction: any) => {
            if (this.props.uiStore.widgets.has(this.widgetKey)) {
                reaction.dispose(); // stop autorun
                return;
            }
            const {view} = this.props;
            if (!view) {
                return;
            }
            this.setState({
                coordinate: null,
                elevation: null
            });
            this.watchHandles.push(
                view.watch("stationary",
                    () =>  {
                        this.resetCursorTimer();
                        this.setState({
                            coordinate: null,
                            elevation: null
                        });
                    })
            );
            this.watchHandles.push(
                reactiveUtils.when(
                    () => view.stationary === true,
                    () => {
                        if (this.elevationLayerIndex >= 0) {
                            this.updateElevationSampler();
                        }
                    })
            );
            this.loadElevationLayer();
            this.watchHandles.push(
                view.watch("map.ground.layers.length",
                    () =>  {
                        this.loadElevationLayer();
                    })
            );
            this.watchHandles.push(
                view.on("pointer-move", (evt) => {
                    this.resetCursorTimer();
                    this.setState({
                        coordinate: view.toMap({ x: evt.x, y: evt.y }),
                        elevation: view.map.ground? -1: null
                    });
                    const mapPoint = view.toMap(evt);
                    if (this.elevationSampler) {
                        this.elevationSampler.then((elevationSampler)=> {
                            const mapPointZ = elevationSampler.queryElevation(mapPoint) as __esri.Point;
                            this.setState({elevation: mapPointZ.z});
                        });
                    }

                })
            );
            this.watchHandles.push(
                view.on("pointer-leave", () => {
                    this.resetCursorTimer();
                    this.setState({
                        coordinate: null,
                        elevation: view.map.ground? -1: null
                    });
                })
            );
            this.setState({
                isLoading: false
            });
            this.props.uiStore.addWidget(this.widgetKey, this);

            reaction.dispose();
        }));
    }
    updateElevationSampler() {
        const {view} = this.props;
        if (!view) {
            return;
        }
        this.setState({
            titleElevationLayer: view.map.ground.layers.getItemAt(this.elevationLayerIndex).title
        });
        if(view.extent){
            // Extent als kopie übergeben damit veränderungen innerhalb des ElevationSampler keinen Einfluss auf die
            // Karte haben. Ansonsten funktioniert die Druckfunktion in der Grenzregion nicht korrekt.
            this.elevationSampler =
                view.map.ground.layers.getItemAt(this.elevationLayerIndex)
                    .createElevationSampler(new Extent(view.extent.toJSON()));
        }else{
            this.elevationSampler = null;
        }
    }

    componentWillUnmount() {
        const {uiStore} = this.props;
        this.watchHandles.forEach((handle: any) => {
            handle.remove();
        });
        this.watchHandles.length = 0;
        this.autorunDisposers.forEach((disposer: any) => {
            disposer();
        });
        this.autorunDisposers.length = 0;
        uiStore.removeWidget(this.widgetKey);
    }
    render(): JSX.Element | null {
        const {coordinate, elevation, titleElevationLayer} = this.state;
        let coords = "";
        let elevationElement: JSX.Element | null = null;
        let className: string;
        if(coordinate){
            className ="show";
            coords =
                "E " +
                new Intl.NumberFormat("de-CH").format(Math.round(coordinate.x)) +
                " / N " +
                new Intl.NumberFormat("de-CH").format(Math.round(coordinate.y));
            if (elevation !== null) {
                if (elevation === -1) {
                    elevationElement = <span className={"geo-elevation"}>
                        Höhe: <span className={"value"}>
                            <Icon name={"recycle"} className={"geo-rotating"} /> m.ü.M
                        </span>
                    </span>;
                } else {
                    // coords += new Intl.NumberFormat("de-CH").format(Math.round(elevation)) + " m.ü.M";
                    elevationElement = <span className={"geo-elevation"}>
                        Höhe: <span className={"value"}>{ (elevation === 0)? "?":
                            new Intl.NumberFormat("de-CH").format(Math.round(elevation))
                        } m.ü.M ({titleElevationLayer})</span></span>;
                }
            }
        } else {
            className ="hide";
        }
        return (
            <div className={"geo-coordinate-elevation "+className}>
                <span className={"geo-coordinate"}>{coords}</span>{elevationElement}</div>
        );
    }
}
